import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaCircle, FaEdit, FaPlus } from "react-icons/fa";
import { FaFileLines } from "react-icons/fa6";
import { apiService } from "../../../components/Config/api.service";
import SalaryEvolutionChart from "../../../components/Tools/SalaryEvolutionChart";
import { canEditHrData } from "../../../components/Auth/permissions";

const InfoRow = ({ label, value, icon }) => (
  <div className="flex justify-between py-2 border-b border-gray-100">
    <span className="text-gray-500 flex items-center gap-2">
      {icon}
      {label}
    </span>
    <span className="font-medium">{value}</span>
  </div>
);

const DocumentCard = ({ name }) => (
  <div className="flex items-center border px-2 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all">
    <div className="p-2 flex gap-2 items-center text-blue-600">
      <FaFileLines />
      <span className="text-sm font-medium">{name}</span>
    </div>
  </div>
);

const Informations = ({ employee, payslipData, setIsDrawerOpen }) => {
  const { t, i18n } = useTranslation();
  const canEdit = canEditHrData();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const { name, maritalStatus, numberOfChildren, spouse_income, nationality, address, email, place_of_birth, bank_name, account_number, ssnid, company_country_code } = employee || {};

  const formatMaritalStatus = (status) => {
    switch (status) {
      case "married": return t('employeeProfile.informations.married');
      case "single": return t('employeeProfile.informations.single');
      case "divorced": return t('employeeProfile.informations.divorced');
      case "widower": return t('employeeProfile.informations.widower');
      default: return t('employeeProfile.informations.unspecified');
    }
  };

  // Solde de congés : celui de la Paie, par la même route que « Mes congés »
  // et la Supervision RH (YRH-FOUND-019, Yros/Kavero#418). Plus de repli sur
  // les compteurs stockés du contrat : ils restent figés tant que le contrat
  // ne change pas (Yros/Kavero#419), et divergeaient des deux autres écrans.
  const [soldesRh, setSoldesRh] = useState([]);
  const [messageSolde, setMessageSolde] = useState(null);

  useEffect(() => {
    let unmounted = false;
    setSoldesRh([]);
    setMessageSolde(null);
    apiService
      .listerSoldeRh(employee?.id)
      .then((res) => {
        if (unmounted) return;
        if (res?.ok && Array.isArray(res.soldes)) {
          setSoldesRh(res.soldes);
        } else {
          setMessageSolde(res?.message || t('employeeProfile.informations.leaveBalanceUnavailable'));
        }
      })
      .catch(() => {
        if (!unmounted) setMessageSolde(t('employeeProfile.informations.leaveBalanceUnavailable'));
      });
    return () => {
      unmounted = true;
    };
    // Un chargement par salarié : `t` change d'identité sans que le solde
    // change, et le relancer à chaque rendu ferait boucler l'appel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  const renderLeaveBalance = () => {
    if (messageSolde) {
      return (
        <p data-testid="informations-solde-message" className="text-sm text-gray-600">
          {messageSolde}
        </p>
      );
    }
    return (
      <div className="space-y-4">
        {soldesRh.map((solde) => {
          const cle = solde.type_conge_id ?? 'paie';
          const acquis = Number(solde.acquis ?? 0);
          const pris = Number(solde.pris ?? 0);
          const restant = Number(solde.restant ?? 0);
          // Coalescence avant conversion : `Number(undefined)` vaut NaN, et
          // `NaN ?? x` reste NaN.
          const restantProjete = Number(solde.restant_projete ?? solde.restant ?? 0);
          const percent = acquis > 0 ? Math.min(100, Math.max(0, Math.round((pris / acquis) * 100))) : 0;

          return (
            <div key={cle} data-testid={`informations-solde-${cle}`} className="border-b border-gray-100 pb-3 last:border-0">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-sm text-gray-800">{solde.libelle}</span>
              </div>
              <div className="w-full bg-gray-200 flex rounded-full h-2 mb-2 overflow-hidden">
                <div className="bg-orange-500 h-2" style={{ width: `${percent}%` }}></div>
                <div className="bg-green-500 h-2" style={{ width: `${100 - percent}%` }}></div>
              </div>
              <div className="space-y-1 text-xs">
                <InfoRow icon={<FaCircle className="text-blue-500 text-xs" />} label={t('employeeProfile.informations.totalAcquired')} value={t('employeeProfile.informations.daysCount', { count: acquis })} />
                <InfoRow icon={<FaCircle className="text-orange-500 text-xs" />} label={t('employeeProfile.informations.taken')} value={t('employeeProfile.informations.daysCount', { count: pris })} />
                <InfoRow icon={<FaCircle className="text-green-500 text-xs" />} label={t('employeeProfile.informations.remainingBalance')} value={t('employeeProfile.informations.daysCount', { count: restant })} />
                <InfoRow icon={<FaCircle className="text-gray-400 text-xs" />} label={t('employeeProfile.informations.afterPendingRequests')} value={t('employeeProfile.informations.daysCount', { count: restantProjete })} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const companyInfoStr = localStorage.getItem('companyInfo');
  const companyInfo = companyInfoStr ? JSON.parse(companyInfoStr) : null;
  const isCoteDIvoire = company_country_code === 'CI' || companyInfo?.country_code === 'CI';

  const salaryHistory = [
    { year: "-", salary: "5 400 000" },
    { year: 2024, salary: "5 000 000" },
    { year: 2023, salary: "3 200 000" },
    { year: 2022, salary: "2 500 000" },
  ];

  return (
    <div className="p-4 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

        {/* Documents d'embauche */}
        {/* <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Documents D'embauche</h3>
              <button className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 text-sm font-medium flex items-center gap-1">
                <FaPlus className="text-xs" /> Ajouter
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DocumentCard name="cv_francais.pdf" />
              <DocumentCard name="contrat_de_travial.pdf" />
            </div>
          </div>
        </div> */}

        {/* Informations personnelles */}
        <div className="col-span-1 md:col-span-2 md:row-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <span className="w-2 h-6 bg-blue-500 rounded-sm mr-2" />
                {t('employeeProfile.informations.personalInfo')}
              </h3>
              {canEdit && (
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium flex items-center gap-1"
              >
                <FaEdit className="text-xs" /> {t('employeeProfile.informations.edit')}
              </button>
              )}
            </div>
            <div className="space-y-3">
              <InfoRow label={t('employeeProfile.informations.fullName')} value={name || "-"} />
              <InfoRow
                label={t(isCoteDIvoire ? 'employeeProfile.informations.cnpsNumber' : 'employeeProfile.informations.socialSecurityNumber')}
                value={ssnid || "-"}
              />
              <InfoRow label={t('employeeProfile.informations.placeOfBirth')} value={place_of_birth || "-"} />
              <InfoRow label={t('employeeProfile.informations.maritalStatus')} value={formatMaritalStatus(maritalStatus)} />
              <InfoRow label={t('employeeProfile.informations.numberOfChildren')} value={numberOfChildren ?? "-"} />
              <InfoRow label={t('employeeProfile.informations.spouseIncome')} value={spouse_income ? t('employeeProfile.informations.yes') : t('employeeProfile.informations.no')} />
              <InfoRow label={t('employeeProfile.informations.nationality')} value={nationality || "-"} />
              <InfoRow label={t('employeeProfile.informations.address')} value={address || "-"} />
              <InfoRow label={t('employeeProfile.informations.email')} value={email || "-"} />
              <div className="pt-2 mt-2 border-t border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-2">{t('employeeProfile.informations.bankDetails')}</h4>
                <InfoRow label={t('employeeProfile.informations.bank')} value={bank_name || "-"} />
                <InfoRow label={t('employeeProfile.informations.accountNumber')} value={account_number || "-"} />
              </div>
            </div>
          </div>
        </div>

        {/* Salaire du mois */}
        <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <h3 className="text-lg font-bold mb-4">{t('employeeProfile.informations.monthSalary')}</h3>
            <div className="space-y-3">
              <InfoRow
                label={t('employeeProfile.informations.grossSalary')}
                value={`${(payslipData?.gross_salary || 0).toLocaleString(dateLocale)} FCFA`}
              />
              <InfoRow
                label={t('employeeProfile.informations.netSalary')}
                value={`${(payslipData?.net_salary || 0).toLocaleString(dateLocale)} FCFA`}
              />
            </div>
          </div>
        </div>

        {/* Solde congés */}
        <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <span className="w-2 h-6 bg-orange-500 rounded-sm mr-2" />
              {t('employeeProfile.informations.leaveBalance')}
            </h3>
            {renderLeaveBalance()}
          </div>
        </div>
      </div>

      {/* Graphique salaire */}
      <div className="mt-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-lg font-bold mb-6">{t('employeeProfile.informations.salaryChart')}</h3>
          <SalaryEvolutionChart employeeId={employee.id} />
        </div>
      </div>
    </div>
  );
};

export default Informations;
